/**
 * 体积数据模型 —— 纯数据结构与计算函数，不依赖任何运行时服务。
 */
import type * as THREE from 'three'

/** 体积计算结果 */
export interface VolumeResult {
  /** 唯一 ID */
  readonly id: string
  /** 体积定义的变换矩阵 */
  readonly matrix: THREE.Matrix4
  /** 体积盒尺寸（本地空间） */
  readonly dimensions: THREE.Vector3
  /** 计算得到的体积值（立方米），null 表示尚未计算 */
  computedVolume: number | null
}

import { createIdGenerator } from '../shared/idGenerator'

/** 生成唯一体积 ID */
export const generateVolumeId = createIdGenerator('volume')

/**
 * 计算长方体体积 = abs(x * y * z)。
 *
 * @param dimensions - 长方体在本地空间的尺寸
 * @returns 体积值，单位与输入尺寸立方一致
 */
export function computeVolume(dimensions: THREE.Vector3): number {
  return Math.abs(dimensions.x * dimensions.y * dimensions.z)
}

/**
 * 根据变换矩阵和尺寸构建完整的体积结果。
 *
 * @param matrix - 体积在世界空间的变换矩阵，将被克隆
 * @param dimensions - 本地空间尺寸，将被克隆
 * @returns 体积结果，含自动生成的 ID 与计算后的体积值
 */
export function buildVolumeResult(matrix: THREE.Matrix4, dimensions: THREE.Vector3): VolumeResult {
  return {
    id: generateVolumeId(),
    matrix: matrix.clone(),
    dimensions: dimensions.clone(),
    computedVolume: computeVolume(dimensions),
  }
}
